import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Breadcrumbs,
  Link,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

const PrivacyPolicyPage: React.FC = () => {
  const lastUpdated = 'May 15, 2023';

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ bgcolor: '#1a365d', color: 'white', py: 6, pt: 4 }}>
        <LandingHeader />
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
            Privacy Policy
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9, mb: 2 }}>
            Last Updated: {lastUpdated}
          </Typography>
          
          <Breadcrumbs aria-label="breadcrumb" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <Link color="inherit" href="/">
              Home
            </Link>
            <Typography color="rgba(255,255,255,0.9)">Privacy Policy</Typography>
          </Breadcrumbs>
        </Container>
      </Box>
      
      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
          <Typography variant="body1" paragraph>
            At Contract AI, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform and services. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the platform.
          </Typography>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            1. Information We Collect
          </Typography>
          <Typography variant="body1" paragraph>
            We collect information that you provide directly to us when you register for an account, create or modify your profile, set preferences, or upload or submit any information through our platform. This information may include:
          </Typography>
          <List sx={{ pl: 4 }}>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="Personal identification information (name, email address, phone number)" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="Business information (company name, job title, department)" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="User content (documents, contracts, notes, and other materials you upload, submit, store, or generate through your use of the Services)" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="Payment information (credit card details, billing address)" />
            </ListItem>
          </List>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            2. How We Use Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We use the information we collect for various purposes, including to:
          </Typography>
          <List sx={{ pl: 4 }}>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="Provide, maintain, and improve our Services" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="Process transactions and send related information" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="Send administrative information, such as updates, security alerts, and support messages" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="Respond to your comments, questions, and requests" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="Improve and develop new features and services" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="Monitor and analyze usage patterns and trends" />
            </ListItem>
          </List>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            3. How We Share Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We may share your information in the following situations:
          </Typography>
          <List sx={{ pl: 4 }}>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText 
                primary="With service providers" 
                secondary="We may share your information with third-party vendors, service providers, contractors, or agents who perform services for us or on our behalf." 
              />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText 
                primary="With your consent" 
                secondary="We may disclose your personal information for any purpose with your consent." 
              />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText 
                primary="For legal purposes" 
                secondary="We may disclose your information to comply with applicable laws and regulations, to respond to a subpoena, search warrant, or other lawful request for information we receive, or to otherwise protect our rights." 
              />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText 
                primary="Business transfers" 
                secondary="If we are involved in a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction." 
              />
            </ListItem>
          </List>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            4. Data Security
          </Typography>
          <Typography variant="body1" paragraph>
            We implement appropriate technical and organizational measures to protect the security of your personal information. However, please note that no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
          </Typography>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            5. Your Data Protection Rights
          </Typography>
          <Typography variant="body1" paragraph>
            Depending on your location, you may have the following rights regarding your personal information:
          </Typography>
          <List sx={{ pl: 4 }}>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="The right to access the personal information we have about you" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="The right to request correction of inaccurate personal information" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="The right to request deletion of your personal information" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="The right to object to processing of your personal information" />
            </ListItem>
            <ListItem sx={{ display: 'list-item', listStyleType: 'disc' }}>
              <ListItemText primary="The right to data portability" />
            </ListItem>
          </List>
          <Typography variant="body1" paragraph sx={{ mt: 2 }}>
            To exercise these rights, please contact us at privacy@contractai.com.
          </Typography>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            6. Children's Privacy
          </Typography>
          <Typography variant="body1" paragraph>
            Our Services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and believe that your child has provided us with personal information, please contact us so that we can take steps to delete such information.
          </Typography>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            7. Changes to This Privacy Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this privacy policy from time to time to reflect changes to our practices or for other operational, legal, or regulatory reasons. We will notify you of any changes by posting the new privacy policy on this page and updating the "Last Updated" date. You are advised to review this privacy policy periodically for any changes.
          </Typography>
          
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            8. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions about this privacy policy or our data practices, please contact us at:
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Contract AI, Inc.<br />
            123 Market Street, Suite 400<br />
            San Francisco, CA 94105<br />
            United States<br />
            Email: privacy@contractai.com<br />
            Phone: +1 (800) 555-0123
          </Typography>
          
          <Divider sx={{ my: 4 }} />
          
          <Box sx={{ bgcolor: '#f8faff', p: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Effective Date
            </Typography>
            <Typography variant="body2">
              This Privacy Policy is effective as of {lastUpdated}.
            </Typography>
          </Box>
        </Paper>
      </Container>
      
      <Footer />
    </Box>
  );
};

export default PrivacyPolicyPage; 